'use no memo';

import { useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useCallback } from 'react';
import { FillScreen, HStack, VStack } from '@/components/Layout';
import { ProgressCircle } from '@/components/Progress';
import { Text } from '@/components/Typography/Text';
import { Separator } from '@/components/ui/separator';
import { operationInstanceQueryKeys } from '@/features/operation-instances/hooks';
import { StepCard } from '@/features/operation-package-instance-profile/-components/StepCard';
import { LabelPerson } from '@/features/person/components/LabelPerson';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { deviceInventoryTaskResolver } from '@/routes/_staff/it-operation-core/-task-code-resolver';
import { usePackage } from './-hooks/usePackage';
import { useTaskOperation } from './-hooks/useTaskOperation';
import { useTodoAction } from './-hooks/useTodoAction';
import { useGetInstanceDetail, useQueryInstanceProgress } from './-queries';
export const Route = createFileRoute('/_staff/it-operation-core/_detail/operation-instances/$instanceId/_layout/detail/')({
  component: RouteComponent
});
function RouteComponent() {
  const {
    id,
    packageType
  } = Route.parentRoute.useLoaderData();
  const {
    Component
  } = usePackage({
    packageType
  });

  // 専用コンポーネントを持つパッケージ
  // 各コンポーネントが自身のレイアウト（padding等）を管理するため、ここではラップしない
  if (Component) {
    return <Component instanceId={id} />;
  }

  // 共通基盤で描画（デフォルト）
  return <PackageInstanceDetail instanceId={id} />;
}

/**
 * ITOC 共通基盤のパッケージ詳細画面コンポーネント
 * 今後追加されるパッケージは原則このコンポーネントを使用する
 */
function PackageInstanceDetail({
  instanceId
}: {
  instanceId: string;
}) {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.itOperationCore'
  });
  const {
    data
  } = useGetInstanceDetail(instanceId);
  const {
    data: progressData
  } = useQueryInstanceProgress(instanceId);
  const todoAction = useTodoAction(instanceId);
  const queryClient = useQueryClient();
  const handleAdminMutationSuccess = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: operationInstanceQueryKeys.detail('IT_OPERATION_CORE', instanceId)
    });
  }, [queryClient, instanceId]);
  const completedCount = progressData?.completedCount ?? 0;
  const totalCount = progressData?.totalCount ?? 0;
  return <FillScreen>
      <HStack className="px-12 py-7 gap-9 h-full">
        <VStack className="w-full border rounded-lg p-12 gap-3 shadow-xs bg-white overflow-auto">
          <HStack className="justify-between items-center">
            <Text variant="h2">{data.name}</Text>
          </HStack>
          {data.description && <Text>{data.description}</Text>}
          <HStack className="border-2 rounded-md">
            <VStack className="p-4 gap-2">
              <Text variant="labelSmall" className="text-neutral-muted-on-fill">
                Requested For
              </Text>
              <Link to="/person/$id/personal-information" params={{
              id: data.target.targetId
            }}>
                <HStack className="items-center">
                  <LabelPerson name={data.target.targetName} avatarUrl={data.target.targetAvatarUrl} size="Small" hoverScale={false} />
                </HStack>
              </Link>
            </VStack>
            <Separator orientation="vertical" />
            <VStack className="p-4 gap-2">
              <Text variant="labelSmall" className="text-neutral-muted-on-fill">
                Assignee
              </Text>
              <Link to="/person/$id" params={{
              id: data.operator.operatorId
            }}>
                <LabelPerson name={data.operator.operatorName} avatarUrl={data.operator.operatorAvatarUrl} size="Small" hoverScale={false} />
              </Link>
            </VStack>
          </HStack>
          <VStack className="gap-4">
            {data.operationSteps.map((step, index) => <StepCard key={step.id} step={step} stepNumber={index + 1} isLastStep={index === data.operationSteps.length - 1} nextStep={data.operationSteps[index + 1]} isPackageAbort={data.status === 'Aborted'} useTaskOperation={useTaskOperation} instanceTarget={data.target} todoAction={todoAction} designationContext={{
            allSteps: data.operationSteps
          }}
          // デバイス棚卸し v2 の「セルフレポートの依頼」は LOGIC タスクで
          // 汎用 TaskItem が操作ボタンを描かないため customContent で補う。
          // 他の taskCode では null を返すので、他パッケージの描画は変わらない。
          taskCodeResolver={deviceInventoryTaskResolver} enableAdminActions onAdminMutationSuccess={handleAdminMutationSuccess} />)}
          </VStack>
        </VStack>

        <div className="h-fit hidden lg:block">
          <ProgressCircle current={completedCount} total={totalCount} title={t('taskProgress')} expandable={false} />
        </div>
      </HStack>
    </FillScreen>;
}